import { useEffect } from "react";
import { Mail, Users, FileText, Search, Bell, Settings, Loader2, BarChart3 } from "lucide-react";
import { useState } from "react";
import { useAdminAuth, useAdminData } from "@/hooks/useAdmin";
import { AdminLoginForm, AdminHeader } from "@/components/admin/AdminAuth";
import { ApplicationsList, SubscribersList } from "@/components/admin/ApplicationsList";
import { ClientsList } from "@/components/admin/ClientsList";
import { BlogManager } from "@/components/admin/BlogManager";
import { NotificationsList, UserManager } from "@/components/admin/UserNotifications";

export default function AdminPage() {
  const { loggedIn, setLoggedIn, currentUser, loginLoading, authApi, handleLogin, logout, tokenRef } = useAdminAuth();
  const {
    stats, contacts, setContacts, newsletters, setNewsletters,
    clients, setClients, posts, setPosts, users, setUsers,
    notifications, setNotifications, loading, loadData,
  } = useAdminData(authApi, loggedIn, setLoggedIn, tokenRef);
  const [tab, setTab] = useState("contacts");

  useEffect(() => {
    if (loggedIn) loadData();
  }, [loggedIn, loadData]);

  if (!loggedIn) {
    return <AdminLoginForm onLogin={handleLogin} loginLoading={loginLoading} />;
  }

  const canEdit = currentUser?.role === "superadmin" || currentUser?.role === "admin";
  const isSuperAdmin = currentUser?.role === "superadmin";
  const isViewerOnly = currentUser?.role === "viewer";

  const allTabs = [
    { key: "contacts", label: "Applications", icon: Mail, count: stats?.new_contacts, roles: ["superadmin", "admin", "auditor"] },
    { key: "newsletters", label: "Subscribers", icon: Users, count: stats?.total_newsletters, roles: ["superadmin", "admin"] },
    { key: "clients", label: "Certified Clients", icon: Search, count: stats?.total_certified_clients, roles: ["superadmin", "admin", "auditor"] },
    { key: "posts", label: "Blog / Insights", icon: FileText, count: stats?.total_blog_posts, roles: ["superadmin", "admin"] },
    { key: "notifications", label: "Notifications", icon: Bell, count: notifications.filter((n) => !n.read).length, roles: ["superadmin", "admin", "auditor"] },
    { key: "users", label: "User Management", icon: Settings, count: stats?.total_admin_users, roles: ["superadmin"] },
  ];
  const tabs = allTabs.filter((t) => t.roles.includes(currentUser?.role));

  const renderTabContent = () => {
    if (loading) return <div className="text-center py-12"><Loader2 className="animate-spin mx-auto text-[#13A8BD]" size={24} /></div>;
    switch (tab) {
      case "contacts":
        return <ApplicationsList contacts={contacts} setContacts={setContacts} authApi={authApi} canEdit={canEdit} />;
      case "newsletters":
        return <SubscribersList newsletters={newsletters} setNewsletters={setNewsletters} authApi={authApi} canEdit={canEdit} />;
      case "clients":
        return <ClientsList clients={clients} setClients={setClients} authApi={authApi} canEdit={canEdit} />;
      case "posts":
        return <BlogManager posts={posts} setPosts={setPosts} authApi={authApi} canEdit={canEdit} />;
      case "notifications":
        return <NotificationsList notifications={notifications} setNotifications={setNotifications} authApi={authApi} />;
      case "users":
        return isSuperAdmin ? <UserManager users={users} setUsers={setUsers} authApi={authApi} loadData={loadData} currentUser={currentUser} /> : null;
      default:
        return null;
    }
  };

  return (
    <div className="page-transition min-h-screen bg-[#F7F9FC]" data-testid="admin-dashboard">
      <AdminHeader currentUser={currentUser} onRefresh={loadData} onLogout={logout} />

      {stats && (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: "Applications", value: stats.total_contacts, accent: `${stats.new_contacts} new`, color: "text-blue-600" },
              { label: "Subscribers", value: stats.total_newsletters, color: "text-green-600" },
              { label: "Certified Clients", value: stats.total_certified_clients, color: "text-[#13A8BD]" },
              { label: "Blog Posts", value: stats.total_blog_posts, accent: `${stats.published_posts} published`, color: "text-purple-600" },
              { label: "Notifications", value: stats.unread_notifications, accent: "unread", color: "text-orange-600" },
            ].map((s) => (
              <div key={s.label} className="bg-white border border-[#0B2545]/10 p-4">
                <div className={`text-2xl font-black ${s.color}`} style={{ fontFamily: "Chivo, sans-serif" }}>{s.value}</div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</p>
                {s.accent && <p className="text-[10px] text-[#13A8BD] font-bold">{s.accent}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {isViewerOnly ? (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="bg-white border border-[#0B2545]/10 p-8 text-center">
            <BarChart3 size={32} className="mx-auto text-[#13A8BD] mb-4" />
            <h3 className="text-lg font-bold text-[#0B2545] mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>Dashboard Overview</h3>
            <p className="text-xs text-slate-500">You have view-only access. Contact your administrator for additional permissions.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex gap-0 border-b border-[#0B2545]/10 overflow-x-auto">
              {tabs.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
                  className={`flex items-center gap-1.5 px-4 py-3 text-[11px] font-bold uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${tab === t.key ? 'border-[#13A8BD] text-[#13A8BD]' : 'border-transparent text-slate-400 hover:text-[#0B2545]'}`}>
                  <t.icon size={14} /> {t.label} {t.count != null && t.count > 0 && <span className="ml-1 bg-[#13A8BD]/10 text-[#13A8BD] px-1.5 py-0.5 text-[9px] font-bold">{t.count}</span>}
                </button>
              ))}
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
            {renderTabContent()}
          </div>
        </>
      )}
    </div>
  );
}
